/* Les six fichiers qu'on sait lire.
 *
 * Chaque modèle décrit les colonnes attendues, leurs synonymes tolérés et ce
 * qui est obligatoire. C'est aussi ce qui sert à fabriquer le fichier d'exemple
 * téléchargeable : un seul endroit à tenir à jour, pas deux qui divergent.
 */

import type { Modele } from "./import";

export type CleImport =
  | "usines" | "produits" | "contacts" | "prix_usine" | "prix_agent" | "agents";

export const DOMAINES = ["customer", "finance", "quality", "packaging", "logistics"] as const;
export const NIVEAUX = ["agent", "admin"] as const;

export const MODELES: Record<CleImport, Modele> = {
  usines: {
    cle: "usines",
    titre: "Usines",
    propos: "Les fabricants. Le code sert de clé : réimporter le même code met la fiche à jour.",
    champs: [
      { cle: "code", titre: "Code", type: "texte", requis: true,
        aide: "court, sans espace — sert de clé (ex. mundiriso)" },
      { cle: "nom", titre: "Nom", type: "texte", requis: true },
      { cle: "categorie", titre: "Catégorie", alias: ["famille"], type: "texte" },
      { cle: "ville", titre: "Ville", type: "texte" },
      { cle: "pays", titre: "Pays", type: "texte" },
      { cle: "prefixe_facture", titre: "Préfixe facture", alias: ["prefixe"], type: "texte",
        aide: "début des numéros de facture de l'usine (ex. MR/2026/)" },
      { cle: "actif", titre: "Actif", type: "booleen", defaut: true },
    ],
  },

  produits: {
    cle: "produits",
    titre: "Produits",
    propos:
      "Le catalogue. « Prix de vente » est le plancher par défaut, celui qui s'applique " +
      "à un agent sans tarif propre. Usine + Référence font la clé.",
    champs: [
      { cle: "usine_id", titre: "Usine", type: "texte", requis: true, reference: "usines",
        aide: "code ou nom d'une usine déjà créée" },
      { cle: "ref", titre: "Référence", alias: ["ref", "code produit"], type: "texte",
        requis: true },
      { cle: "nom", titre: "Nom", alias: ["designation", "libelle"], type: "texte", requis: true },
      { cle: "gamme", titre: "Gamme", type: "texte",
        aide: "sert au barème de commission par gamme" },
      { cle: "conditionnement", titre: "Conditionnement", alias: ["cond", "unite"],
        type: "texte" },
      { cle: "prix", titre: "Prix de vente", alias: ["prix", "prix public"], type: "nombre" },
      { cle: "actif", titre: "Actif", type: "booleen", defaut: true },
    ],
  },

  contacts: {
    cle: "contacts",
    titre: "Contacts",
    propos:
      "Les personnes, chez une usine ou chez un client. Renseigner l'une des deux " +
      "colonnes, pas les deux.",
    champs: [
      { cle: "nom", titre: "Nom", type: "texte", requis: true },
      { cle: "usine_id", titre: "Usine", type: "texte", reference: "usines",
        aide: "code ou nom — laisser vide pour un contact client" },
      { cle: "client_id", titre: "Client", type: "texte", reference: "clients",
        aide: "nom du client — laisser vide pour un contact usine" },
      { cle: "email", titre: "Email", alias: ["mail", "courriel"], type: "texte" },
      { cle: "tel", titre: "Téléphone", alias: ["tel", "telephone"], type: "texte" },
      { cle: "domaines", titre: "Domaines", alias: ["service", "roles"], type: "texte",
        aide: `séparés par une virgule : ${DOMAINES.join(", ")}` },
      { cle: "generique", titre: "Adresse de service", alias: ["generique"], type: "booleen",
        defaut: false, aide: "oui si c'est une boîte partagée, pas une personne" },
    ],
  },

  prix_usine: {
    cle: "prix_usine",
    titre: "Prix usine",
    propos:
      "Ce que l'usine demande. Direction seulement : ces lignes ne descendent jamais " +
      "dans le navigateur d'un agent.",
    champs: [
      { cle: "usine_id", titre: "Usine", type: "texte", requis: true, reference: "usines" },
      { cle: "produit_id", titre: "Référence", alias: ["ref", "produit"], type: "texte",
        requis: true, reference: "produits", aide: "référence du produit, telle qu'au catalogue" },
      { cle: "prix", titre: "Prix usine", alias: ["prix", "prix achat", "tarif usine"],
        type: "nombre", requis: true },
    ],
  },

  prix_agent: {
    cle: "prix_agent",
    titre: "Prix mini par agent",
    propos:
      "Le plancher de vente d'un agent sur une référence. En dessous, le devis " +
      "l'avertit. Au-dessus, il vend ce qu'il veut.",
    champs: [
      { cle: "agent_id", titre: "Agent", type: "texte", requis: true, reference: "agents",
        aide: "nom ou email de l'agent" },
      { cle: "produit_id", titre: "Référence", alias: ["ref", "produit"], type: "texte",
        requis: true, reference: "produits" },
      { cle: "prix_mini", titre: "Prix mini", alias: ["prix", "plancher", "prix mini vente"],
        type: "nombre", requis: true },
    ],
  },

  agents: {
    cle: "agents",
    titre: "Agents",
    propos:
      "Les fiches agents, sans compte de connexion. L'invitation se lance ensuite, " +
      "fiche par fiche, depuis l'écran Agents.",
    champs: [
      { cle: "nom", titre: "Nom", type: "texte", requis: true },
      { cle: "email", titre: "Email", alias: ["mail", "courriel"], type: "texte", requis: true,
        aide: "l'adresse qui recevra l'invitation — sert aussi de clé" },
      { cle: "niveau", titre: "Niveau", type: "liste", valeurs: NIVEAUX, defaut: "agent",
        aide: NIVEAUX.join(", ") },
      { cle: "actif", titre: "Actif", type: "booleen", defaut: true },
    ],
  },
};

export const ORDRE_IMPORT: readonly CleImport[] = [
  "usines", "produits", "contacts", "prix_usine", "prix_agent", "agents",
];

/** Ce qui doit exister avant de pouvoir importer ce type. */
export const PREALABLES: Record<CleImport, string | null> = {
  usines: null,
  produits: "les usines",
  contacts: "les usines et les clients",
  prix_usine: "les produits",
  prix_agent: "les produits et les agents",
  agents: null,
};

/** Niveau requis pour importer ce type. */
export const NIVEAU_REQUIS: Record<CleImport, "agent" | "admin"> = {
  usines: "agent",
  produits: "agent",
  contacts: "agent",
  prix_usine: "admin",
  prix_agent: "admin",
  agents: "admin",
};
